/**
 * Role Prioritisation — types and the seeded baseline.
 *
 * Every role approved at FINN carries a priority rating from the CEO (AAA / A / B / C). This model
 * prices each rating in recruiter hours per week, sums that across the portfolio, and compares the
 * result against what the team can actually commit.
 *
 * WHAT IS LIVE AND WHAT IS SEEDED. The effort model, the capacity arithmetic and the portfolio come
 * from baseline v3, agreed by the TA team on 7 September 2026. The split of those roles across
 * clusters and the individual working models are ILLUSTRATIVE: Airtable is not connected yet, and
 * no system holds per-recruiter contracted hours. Both are surfaced as such in the UI rather than
 * presented as fact — see SEED_PROVENANCE.
 */

export type Rating = 'AAA' | 'A' | 'B' | 'C';

/** Ordered most effort to least. Order is load-bearing: the trade-off ladder walks it. */
export const RATINGS: readonly Rating[] = ['AAA', 'A', 'B', 'C'] as const;

/** The five activity columns are fixed in v1 — no dynamic activity editor. */
export interface Effort {
  sourcing: number;
  candidateManagement: number;
  interviewing: number;
  hmSync: number;
  atsAdmin: number;
}

/** One line of the effort model: what a recruiter spends the hours on. */
export interface Activity {
  id: keyof Effort;
  label: string;
}

export const ACTIVITIES: readonly Activity[] = [
  { id: 'sourcing', label: 'Sourcing' },
  { id: 'candidateManagement', label: 'Candidate management' },
  { id: 'interviewing', label: 'Interviewing' },
  { id: 'hmSync', label: 'Hiring manager sync' },
  { id: 'atsAdmin', label: 'ATS and admin' },
] as const;

export type EffortModel = Record<Rating, Effort>;
export type Portfolio = Record<Rating, number>;

export interface CapacityAssumptions {
  /** Contracted hours of a 1.0 FTE week. */
  fullWeekHours: number;
  /** Non-req work: rituals, HM enablement, employer branding, tooling, TA's own hiring. */
  overheadPct: number;
  /** Absence and volatility. Planning at zero guarantees the plan breaks. */
  bufferPct: number;
}

/**
 * One person's contracted week. `cluster` is UNASSIGNED while they carry no cluster's load.
 *
 * `countsTowardHiring` is the flag that keeps a team lead's hours OUT of the plan. Somebody can be
 * on the team and not be planned as recruiting capacity — the Head of TA, a coordinator, someone on
 * notice. Default is true; false means the hours are recorded but excluded from every capacity
 * figure, until the person chooses to lend them from the support board.
 */
export interface WorkingModel {
  name: string;
  role: string;
  cluster: string;
  hours: number;
  countsTowardHiring?: boolean;
  /** An intern's hours are worth this share of a recruiter's. Recruiters leave it unset. */
  isIntern?: boolean;
}

export const UNASSIGNED = 'Unassigned';

/** Illustrative distribution of the seeded portfolio across the three clusters. */
export interface ClusterSeed {
  key: string;
  roles: Portfolio;
}

/**
 * Part of one person's week, lent to another cluster. `focus` records WHICH work the support
 * covers; it is intent for the receiving lead, deliberately not an input to the arithmetic, which
 * stays cluster-level.
 */
export interface Allocation {
  /** Index into PrioritisationState.team. */
  from: number;
  to: string;
  hours: number;
  focus: Rating | '';
  /**
   * When present, the row was individually confirmed and carries over to the next weekly as a
   * default the lead can Confirm again or Reset. When absent, the row is a draft. Independent from
   * the top-level `commitment`, which is the whole-batch "Commit until X" pattern the tab keeps —
   * carrying rows over stops the weekly starting from an empty board when nothing structural has
   * changed, and does not lock the plan.
   */
  committedAt?: string;
}

/**
 * The commitment made at a cluster-lead weekly, in force until the next one.
 *
 * `validUntil` is for display; `validUntilIso` is what expiry is judged on. A commitment whose date
 * has passed is not in force, and the board must stop presenting it as though it were.
 */
export interface SupportCommitment {
  by: string;
  at: string;
  validUntil: string;
  validUntilIso: string;
}

/**
 * The live open-role portfolio, as the proxy returns it.
 *
 * `source` is load-bearing and rendered: a lead has to know whether the figure they are about to
 * commit capacity against came from Airtable or from the seeded baseline.
 */
export interface LivePortfolio {
  source: 'airtable' | 'unavailable';
  reason?: string;
  syncedAt: string;
  clusterKeys?: string[];
  openRoles?: number;
  portfolio?: Portfolio;
  clusters?: { key: string; roles: Portfolio; unrated: number; roleCount: number }[];
  /** Open roles carrying no Approved Priority Rating: counted, never priced, always named. */
  unrated?: { title: string; recruiter: string; unit: string; positionId: string; cluster: string | null }[];
  hygiene?: {
    unattributed: { title: string; recruiter: string; unit: string }[];
    unresolvedReasons: { reason: string; count: number }[];
    /** Placed by the recruiter map because People Team was blank — the row is what to fix. */
    byRecruiterFallback?: { title: string; recruiter: string; cluster: string }[];
    crossCluster: { title: string; carriedBy: string; sitsIn: string; recruiter: string }[];
  };
}

/**
 * One recorded edit. `by` is whoever was signed in when it happened.
 *
 * ⚠️ While the state lives in localStorage this can only ever record edits made in THIS browser,
 * so `by` is always the reader themselves. It becomes a real audit trail the moment the state moves
 * server-side (open decision 3) — the log is built now so that move needs no UI work, and the
 * section says so on screen rather than implying a shared history that does not exist yet.
 */
export interface ChangeLogEntry {
  /** ISO timestamp of the most recent edit folded into this entry. */
  at: string;
  by: string;
  /** What changed, in the words the UI shows: "Anna Thiel · 40 h → 20 h". */
  what: string;
}

/** Newest first, and bounded — a scratchpad log is not an archive. */
export const CHANGE_LOG_LIMIT = 40;

/**
 * Consecutive edits to the SAME thing by the same person inside this window fold into one entry.
 * A number input fires per keystroke, so "AAA interviewing 4 → 4.5" would otherwise write four
 * entries and push everything else out of a 40-entry log.
 */
export const CHANGE_LOG_COALESCE_MS = 5 * 60 * 1000;

export interface PrioritisationState {
  team: WorkingModel[];
  /**
   * The effort model and capacity assumptions are STATE, not constants: they are the team's own
   * estimate (baseline v3, calibrated 2026-09-07) and the whole point of the section is that they
   * can be re-cut when the estimate is wrong. `reconcileState` clamps them — a 100% overhead or a
   * zero-hour week would divide every utilisation by nothing.
   */
  model: EffortModel;
  cap: CapacityAssumptions;
  /** Newest first. See ChangeLogEntry for what it can and cannot claim. */
  log: ChangeLogEntry[];
  /** Intern FTE assigned per cluster, drawn from the shared pool. */
  interns: Record<string, number>;
  /** Recruiter FTE out of office per cluster. */
  out: Record<string, number>;
  /**
   * Intern FTE out of office. There is deliberately NO `poolCount` beside it: how much intern
   * capacity exists is derived from the team's intern rows (`internPoolFte`), and a stored count
   * next to it would be a second answer to that question — which is how the tab once planned on an
   * intern nobody had added.
   */
  poolOut: number;
  allocations: Allocation[];
  /** Scenario: roles arriving on top of the approved portfolio. Never written back. */
  added: Portfolio;
  commitment: SupportCommitment | null;
}

// ─────────────────────────── seeded baseline v3 ───────────────────────────

export const BASELINE_VERSION = 3;
export const BASELINE_CALIBRATED_AT = '2026-09-07';

export const SEED_EFFORT: EffortModel = {
  AAA: { sourcing: 2, candidateManagement: 2, interviewing: 4, hmSync: 1, atsAdmin: 1 },
  A: { sourcing: 1, candidateManagement: 1.5, interviewing: 2.5, hmSync: 0.75, atsAdmin: 0.75 },
  B: { sourcing: 0.5, candidateManagement: 1, interviewing: 1.5, hmSync: 0.5, atsAdmin: 0.5 },
  C: { sourcing: 0, candidateManagement: 0.75, interviewing: 0, hmSync: 0.25, atsAdmin: 0.15 },
};

export const SEED_PORTFOLIO: Portfolio = { AAA: 2, A: 10, B: 18, C: 5 };

export const SEED_CAPACITY: CapacityAssumptions = {
  fullWeekHours: 40,
  overheadPct: 20,
  bufferPct: 10,
};

/**
 * Illustrative only. Reconciles exactly to SEED_PORTFOLIO (AAA 2 · A 10 · B 18 · C 5 = 162.75 h)
 * so the cluster view can never disagree with the company total, but which cluster each role
 * actually sits in needs Airtable.
 */
export const SEED_CLUSTERS: ClusterSeed[] = [
  { key: 'Growth', roles: { AAA: 0, A: 3, B: 11, C: 2 } },
  { key: 'Operations', roles: { AAA: 1, A: 5, B: 4, C: 1 } },
  { key: 'Core Functions', roles: { AAA: 1, A: 2, B: 3, C: 2 } },
];

/**
 * Names and cluster attribution come from the recruiter map in `proxy-server/lib/taCheckinConfig.js`
 * — the same map the Check-In tab uses. The contracted hours are assumed: nothing holds them today.
 * They sum to 240 h = 6.0 FTE, which is the brief's figure.
 */
export const SEED_TEAM: WorkingModel[] = [
  { name: 'Lola Hense', role: 'Recruiter', cluster: 'Growth', hours: 40 },
  { name: 'Annika Vestewig', role: 'Recruiter', cluster: 'Growth', hours: 40 },
  { name: 'Louisa Budde', role: 'Recruiter', cluster: 'Growth', hours: 20 },
  { name: 'Luisa Heinzinger', role: 'Recruiter', cluster: 'Operations', hours: 40 },
  { name: 'Antonia Conrad', role: 'Recruiter', cluster: 'Operations', hours: 40 },
  { name: 'Anna Thiel', role: 'Recruiter', cluster: 'Core Functions', hours: 40 },
  { name: 'Annika Lutzenberger', role: 'Recruiter', cluster: 'Core Functions', hours: 20 },
  // On the team, deliberately NOT planned as hiring capacity. The cockpit must never assume the
  // lead is recruiting; lending those hours is a decision taken on the support board.
  {
    name: 'Theresa Machnik',
    role: 'Head of TA',
    cluster: 'Core Functions',
    hours: 40,
    countsTowardHiring: false,
  },
];

/**
 * A person added from the support board. Interns are not seeded: T3 has no intern support, and the
 * intern controls only appear once one is actually on the team.
 */
export function newIntern(name = 'TA intern'): WorkingModel {
  return { name, role: 'Working student', cluster: UNASSIGNED, hours: 16, isIntern: true };
}

/**
 * ⚠️ There is no seeded intern pool any more, and no free-standing pool COUNT.
 *
 * It used to be a number typed into a bar above the cluster cards, unconnected to the team — so the
 * tab could plan on an intern nobody had added, and the working-model table and the pool bar could
 * disagree about how many existed. Intern capacity is now whatever intern rows the team table
 * holds, which means the intern controls simply do not render when there are none. T3 has none.
 */
export const SEED_POOL = { out: 0 };

/** Contracted-week options offered per person. */
export const WORKING_MODELS: readonly { hours: number; label: string }[] = [
  { hours: 40, label: 'Full-time · 40 h' },
  { hours: 32, label: 'Four-day week · 32 h' },
  { hours: 30, label: 'Reduced · 30 h' },
  { hours: 24, label: 'Part-time · 24 h' },
  { hours: 20, label: 'Part-time · 20 h' },
  { hours: 16, label: 'Working student · 16 h' },
  { hours: 0, label: 'On leave · 0 h' },
] as const;

/** What a reader must be told is not yet live. Rendered, not just documented. */
export const SEED_PROVENANCE = {
  portfolio: 'Seeded baseline v3 — Airtable not connected',
  clusterSplit: 'Illustrative split, reconciles to the approved portfolio',
  workingModels: 'Assumed contracted hours — no system holds them today',
} as const;

/** Utilisation bands from the brief: ≤85% headroom, ≤100% at the limit, above that overcommitted. */
export type LoadStatus = 'headroom' | 'limit' | 'over';

/** Above this share of committed capacity on AAA + A the team has no maintenance layer left. */
export const FIREFIGHTING_GUARDRAIL_PCT = 70;

export function seedState(): PrioritisationState {
  const interns: Record<string, number> = {};
  const out: Record<string, number> = {};
  SEED_CLUSTERS.forEach((c) => {
    interns[c.key] = 0;
    out[c.key] = 0;
  });
  return {
    team: SEED_TEAM.map((p) => ({ ...p })),
    interns,
    out,
    poolOut: SEED_POOL.out,
    allocations: [],
    added: { AAA: 0, A: 0, B: 0, C: 0 },
    commitment: null,
    model: cloneEffort(SEED_EFFORT),
    cap: { ...SEED_CAPACITY },
    log: [],
  };
}

/** The model is nested one level, so a shallow copy would share every rating's activity object. */
export function cloneEffort(model: EffortModel): EffortModel {
  return RATINGS.reduce((acc, r) => {
    acc[r] = { ...model[r] };
    return acc;
  }, {} as EffortModel);
}
